import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  Button,
  Text,
  Heading
} from '@chakra-ui/react';
import { MdCancel, MdCheck } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useTickets } from '@hooks/useTickets';
import { formatDate } from '@utils/dateUtils';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { TICKET_UPDATE_FORM_CONFIG } from '@views/OutboundCallingDashboard/utils';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE, ROLES } from '@constants';
import useAuthRole from '@hooks/useAuthRole';
import { MAX_NOTE_LENGTH } from '@views/OutboundCallingDashboard/constant';

/**
 * Action Button Component
 */
const ActionButton = ({
  onClick,
  isLoading,
  isDisabled,
  colorScheme,
  leftIcon,
  children
}) => (
  <Button
    variant="outline"
    colorScheme={colorScheme}
    size="sm"
    border="2px solid"
    borderRadius="xl"
    isDisabled={isDisabled}
    isLoading={isLoading}
    onClick={onClick}
    leftIcon={leftIcon}
  >
    {children}
  </Button>
);

/**
 * Ticket Actions Panel Component
 * Manage ticket status, priority, and notes
 */
const TicketActionsPanel = ({ ticket, onTicketUpdate }) => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();

  const { updateTicket, isUpdatingTicket } = useTickets();

  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form data with both status and note
  useEffect(() => {
    const config = TICKET_UPDATE_FORM_CONFIG(ticket.status);
    const initialData = config.reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});
    setFormData(initialData);
  }, []);

  // Update status field when ticket.status changes, but preserve note
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      status: ticket.status
    }));
  }, [ticket.status]);

  /**
   * Check if there are unsaved changes
   */
  useEffect(() => {
    const statusChanged = formData.status !== ticket.status;
    const noteHasContent = formData.note?.trim().length > 0;
    setHasChanges(statusChanged || noteHasContent);
  }, [formData, ticket.status]);

  /**
   * Handle form input change (for both status and note)
   */
  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? Number(value)
          : value
    }));
  };

  /**
   * Handle save changes (unified for both status and note)
   */
  const handleSaveChanges = async () => {
    const config = TICKET_UPDATE_FORM_CONFIG(ticket.status);
    const isValid = config.every((field) => {
      if (field.isRequired) {
        return (
          formData[field.name] !== undefined &&
          formData[field.name] !== null &&
          formData[field.name] !== ''
        );
      }
      return true;
    });

    if (!isValid) {
      dispatch(
        setAlert({
          title: 'Please fill all required fields',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    try {
      // Build update payload
      const updatePayload = {
        status: formData.status
      };

      // Add note to payload if it has content
      if (formData.note?.trim()) {
        updatePayload.agentRemarks = formData.note.trim();
      }

      await updateTicket(ticket.ticketId, updatePayload);

      // Clear note field after successful save but keep status
      setFormData((prev) => ({ ...prev, note: '' }));

      // Refresh tickets list
      onTicketUpdate && onTicketUpdate();
    } catch (error) {
      console.error('Failed to update ticket:', error);
    }
  };

  /**
   * Handle discard changes
   */
  const handleDiscardChanges = () => {
    const config = TICKET_UPDATE_FORM_CONFIG(ticket.status);
    const initialData = config.reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});
    setFormData(initialData);
    setHasChanges(false);
  };

  /**
   * Calculate remaining characters for note
   */
  const remainingChars = MAX_NOTE_LENGTH - (formData.note?.length || 0);

  return (
    <VStack spacing={4} align="stretch">
      {/* Unified Ticket Update Form */}
      <Box
        bg={'blackAlpha.500'}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'stretch'}
        gap={4}
        p={4}
        borderRadius="lg"
        border="1px solid"
        borderColor="whiteAlpha.300"
      >
        <Heading fontSize="md" color="white">
          Update Ticket
        </Heading>

        <FormControl>
          <VStack alignItems="stretch" spacing={4}>
            {/* All Form Fields */}
            {TICKET_UPDATE_FORM_CONFIG(ticket.status).map((field, index) => (
              <Box key={index}>
                <DynamicFormField
                  fieldConfig={field}
                  data={formData}
                  handleChange={handleInputChange}
                />
                {/* Show character count for note field */}
                {field.key === 'note' && (
                  <Text
                    fontSize="xs"
                    color={remainingChars < 50 ? 'yellow.400' : 'gray.500'}
                    textAlign="right"
                    mt={1}
                  >
                    {remainingChars} characters remaining
                  </Text>
                )}
              </Box>
            ))}

            {/* Save/Discard Buttons */}
            {hasChanges && hasAnyAuthority(ROLES.OUTBOUND_CALLING_AGENT) && (
              <HStack justify="flex-end" spacing={3} pt={2}>
                <ActionButton
                  onClick={handleDiscardChanges}
                  isDisabled={isUpdatingTicket}
                  colorScheme="red"
                  leftIcon={<MdCancel />}
                >
                  Discard Changes
                </ActionButton>
                <ActionButton
                  onClick={handleSaveChanges}
                  isLoading={isUpdatingTicket}
                  colorScheme="brand"
                  leftIcon={<MdCheck />}
                >
                  Save Changes
                </ActionButton>
              </HStack>
            )}
          </VStack>
        </FormControl>
      </Box>

      {/* Existing Notes Display */}
      {ticket.notes && ticket.notes.length > 0 && (
        <Box
          bg={'blackAlpha.500'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'stretch'}
          gap={4}
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          <Heading fontSize="md" color="white">
            Previous Notes
          </Heading>
          <VStack spacing={3} align="stretch" maxH="300px" overflowY="auto">
            {ticket.notes.map((ticketNote) => (
              <Box
                key={ticketNote.id}
                bg="whiteAlpha.50"
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="semibold" color="white">
                      {ticketNote.createdBy}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatDate(ticketNote.createdAt)}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.300">
                    {ticketNote.text}
                  </Text>
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default TicketActionsPanel;
